/**
 * Recently opened chats, frames and tasks, kept on this device only
 * (search hub spec §6: “the last 20 opened chats, frames and tasks”).
 */
import { useSyncExternalStore } from "react";
import type { DurableConversation } from "@/api/conversations";
import type { Task } from "@/api/tasks";
import { accountScopedStorage } from "@/lib/account-scoped-storage";
import { conversationDisplayName } from "@/lib/conversation-display";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";

export type RecentOpenedKind = "chat" | "frame" | "task";

export interface RecentOpened {
  kind: RecentOpenedKind;
  id: string;
  title: string;
  projectId: string | null;
  projectName: string | null;
  frameType: string | null;
  openedAt: number;
}

export const RECENT_OPENED_LIMIT = 20;
const storage = accountScopedStorage("denker.recent-opened.v2:");
const NONE: RecentOpened[] = [];

let snapshot: { raw: string | null; items: RecentOpened[] } = {
  raw: null,
  items: NONE,
};

function isRecentOpened(value: unknown): value is RecentOpened {
  const item = value as Partial<RecentOpened> | null;
  return Boolean(
    item &&
    (item.kind === "chat" || item.kind === "frame" || item.kind === "task") &&
    typeof item.id === "string" &&
    typeof item.title === "string" &&
    typeof item.openedAt === "number",
  );
}

function parse(raw: string | null): RecentOpened[] {
  if (!raw) return NONE;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentOpened) : NONE;
  } catch {
    return NONE;
  }
}

/** The stored list; the same array until the stored value changes. */
export function readRecentOpened(): RecentOpened[] {
  const raw = storage.read();
  if (raw !== snapshot.raw) snapshot = { raw, items: parse(raw) };
  return snapshot.items;
}

export function recordRecentOpened(
  entry: Omit<RecentOpened, "openedAt">,
  now: number = Date.now(),
): void {
  const rest = readRecentOpened().filter(
    (item) => !(item.kind === entry.kind && item.id === entry.id),
  );
  storage.write(
    JSON.stringify(
      [{ ...entry, openedAt: now }, ...rest].slice(0, RECENT_OPENED_LIMIT),
    ),
  );
}

export function clearRecentOpened(): void {
  storage.remove();
}

export function recordRecentChat(conversation: DurableConversation): void {
  recordRecentOpened({
    kind: "chat",
    id: conversation.id,
    title: conversationDisplayName(conversation),
    projectId: conversation.project_id,
    projectName: conversation.project_name,
    frameType: null,
  });
}

export function recordRecentFrame(frameId: string): void {
  const frame = useFramesStore.getState().framesById[frameId];
  if (!frame) return;
  const project = useProjectStore.getState().currentProject;
  const frameType = String(frame.frameType ?? "");
  recordRecentOpened({
    kind: "frame",
    id: frameId,
    title: String(frame.title ?? "").trim() || frameType,
    projectId: project?.id ?? null,
    projectName: project?.name ?? null,
    frameType,
  });
}

export function recordRecentTask(
  task: Pick<Task, "id" | "title" | "project">,
): void {
  const project = useProjectStore
    .getState()
    .projects.find((candidate) => candidate.id === task.project);
  recordRecentOpened({
    kind: "task",
    id: task.id,
    title: task.title,
    projectId: task.project ?? null,
    projectName: project?.name ?? null,
    frameType: null,
  });
}

export function useRecentOpened(): RecentOpened[] {
  return useSyncExternalStore(storage.subscribe, readRecentOpened, () => NONE);
}
